import React, {useState, useEffect, useCallback} from 'react';
import {View, StyleSheet, TouchableOpacity} from 'react-native';
import CCText from '@/Atoms/CCText';
import CCTextInput from '@/Atoms/CCTextInput';
import Box from '@/Atoms/Box';
import Ionicons from 'react-native-vector-icons/Ionicons';
import useSaveTournamentQuestionsResponse from '@/Hooks/GetTournaments/useSaveTournamentQuestionsResponse';
import {TournamentQuestion} from './types/registrationTypes';

const Sparkle = ({size = 14, color = '#806234'}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true">
    <path
      d="M8 1l1.6 4.4L14 7l-4.4 1.6L8 13l-1.6-4.4L2 7l4.4-1.6L8 1z"
      fill={color}
    />
  </svg>
);

interface ProgressiveQuestionsProps {
  questions: TournamentQuestion[];
  userData: any;
  selectedTournament: any;
  onComplete: () => void;
  onBack?: () => void;
}

const AUTOFILL_FIELDS: Record<string, string> = {
  email: 'email',
  mobile_number: 'mobile_number',
  phone: 'mobile_number',
  name: 'player_name',
  player_name: 'player_name',
  dob: 'dob',
  school_name: 'school_name',
  fide_id: 'fide_id',
};

function getKeyboardType(type: string) {
  switch (type) {
    case 'email':
      return 'email-address';
    case 'tel':
      return 'phone-pad';
    case 'number':
      return 'numeric';
    default:
      return 'default';
  }
}

function isAnswered(
  q: TournamentQuestion,
  val: string | string[] | boolean | undefined,
): boolean {
  if (q.question_type === 'multi') {
    return Array.isArray(val) && val.length > 0;
  }
  if (q.question_type === 'consent') {
    return val === true;
  }
  if (typeof val === 'string') {
    return val.trim().length > 0;
  }
  return val != null && val !== '';
}

function QuestionInput({
  question,
  value,
  onChange,
}: {
  question: TournamentQuestion;
  value: any;
  onChange: (val: any) => void;
}) {
  switch (question.question_type) {
    case 'textarea':
      return (
        <CCTextInput
          multiline
          numberOfLines={4}
          placeholder={question.placeholder_text || 'Type your answer...'}
          onChangeText={onChange}
          value={value || ''}
          style={[styles.inputLg, styles.textareaInput]}
        />
      );

    case 'select':
      return (
        <View style={styles.selectWrap}>
          {question.options.map(opt => {
            const isSelected = value === opt;
            return (
              <TouchableOpacity
                key={opt}
                style={[styles.choiceCard, isSelected && styles.choiceCardSel]}
                onPress={() => onChange(opt)}>
                <View
                  style={[
                    styles.radioOuter,
                    isSelected && styles.radioOuterSel,
                  ]}>
                  {isSelected && <View style={styles.radioDot} />}
                </View>
                <CCText
                  fontSize={13}
                  fontWeight={isSelected ? 'semibold' : 'regular'}
                  color="#262322">
                  {opt}
                </CCText>
              </TouchableOpacity>
            );
          })}
        </View>
      );

    case 'choice':
      return (
        <View style={styles.choiceWrap}>
          {question.options.map(opt => {
            const isSelected = value === opt;
            return (
              <TouchableOpacity
                key={opt}
                style={[styles.choiceCard, isSelected && styles.choiceCardSel]}
                onPress={() => onChange(opt)}>
                <View
                  style={[
                    styles.radioOuter,
                    isSelected && styles.radioOuterSel,
                  ]}>
                  {isSelected && <View style={styles.radioDot} />}
                </View>
                <View style={{flex: 1}}>
                  <CCText fontSize={13} fontWeight="semibold" color="#262322">
                    {opt}
                  </CCText>
                </View>
              </TouchableOpacity>
            );
          })}
        </View>
      );

    case 'multi': {
      const arr: string[] = Array.isArray(value) ? value : [];
      const toggle = (opt: string) => {
        onChange(
          arr.includes(opt) ? arr.filter(x => x !== opt) : [...arr, opt],
        );
      };
      return (
        <View style={styles.multiWrap}>
          {question.options.map(opt => {
            const isSelected = arr.includes(opt);
            return (
              <TouchableOpacity
                key={opt}
                style={[styles.multiChip, isSelected && styles.multiChipSel]}
                onPress={() => toggle(opt)}>
                <View
                  style={[
                    styles.checkOuter,
                    isSelected && styles.checkOuterSel,
                  ]}>
                  {isSelected && (
                    <Ionicons name="checkmark" size={12} color="#FFFFFF" />
                  )}
                </View>
                <CCText fontSize={13} color="#262322">
                  {opt}
                </CCText>
              </TouchableOpacity>
            );
          })}
        </View>
      );
    }

    case 'consent':
      return (
        <TouchableOpacity
          style={[styles.consentRow, value && styles.consentRowSel]}
          onPress={() => onChange(!value)}>
          <View style={[styles.checkOuter, value && styles.checkOuterSel]}>
            {value && <Ionicons name="checkmark" size={12} color="#FFFFFF" />}
          </View>
          <CCText fontSize={13} color="#4D3F37" style={styles.consentText}>
            {question.question_text}
          </CCText>
        </TouchableOpacity>
      );

    default:
      return (
        <CCTextInput
          placeholder={question.placeholder_text || 'Type your answer...'}
          keyboardType={getKeyboardType(question.question_type)}
          onChangeText={onChange}
          value={value || ''}
          style={styles.inputLg}
        />
      );
  }
}

export default function ProgressiveQuestions({
  questions,
  userData,
  selectedTournament,
  onComplete,
  onBack,
}: ProgressiveQuestionsProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState<Record<number, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const {mutate: submitQuestions, data: submitResponse} =
    useSaveTournamentQuestionsResponse();

  useEffect(() => {
    if (submitResponse) {
      setIsSubmitting(false);
      onComplete();
    }
  }, [submitResponse, onComplete]);

  const q = questions[currentIndex];
  if (!q) {
    return null;
  }

  const val = responses[q.id];
  const answered = isAnswered(q, val);
  const isMandatory = q.is_mandatory === true || q.is_mandatory === 1;
  const canNext = isMandatory ? answered : true;
  const isLast = currentIndex === questions.length - 1;
  const progress = ((currentIndex + 1) / questions.length) * 100;

  const autofillKey = q.field_to_update
    ? AUTOFILL_FIELDS[q.field_to_update]
    : null;
  const autofillValue =
    autofillKey && userData?.[autofillKey] ? userData[autofillKey] : null;

  const setVal = (v: any) => {
    setResponses(prev => ({...prev, [q.id]: v}));
    setValidationError(null);
  };

  const validateAndProceed = useCallback(() => {
    if (q.validator_regex && typeof val === 'string') {
      try {
        const regex = new RegExp(q.validator_regex);
        if (!regex.test(val)) {
          setValidationError('Please enter a valid response');
          return;
        }
      } catch (_) {}
    }
    setValidationError(null);

    if (isLast) {
      setIsSubmitting(true);
      const formattedResponses = Object.entries(responses).map(
        ([questionId, response]) => ({
          question_id: Number(questionId),
          response: Array.isArray(response)
            ? response.join(',')
            : typeof response === 'boolean'
            ? response
              ? 'Yes'
              : 'No'
            : String(response || ''),
        }),
      );
      submitQuestions({
        tournament_id: selectedTournament?.registration_tid,
        player_id: userData?.player,
        responses: formattedResponses,
        sender: userData?.mobile_number,
      });
    } else {
      setCurrentIndex(i => i + 1);
    }
  }, [
    q,
    val,
    isLast,
    responses,
    selectedTournament,
    userData,
    submitQuestions,
  ]);

  const backDisabled = currentIndex === 0 && !onBack;
  const nextDisabled = !canNext || isSubmitting;

  return (
    <Box>
      {/* Progress bar */}
      <View style={styles.progressTrack}>
        <View
          style={[
            styles.progressFill,
            {
              width: `${progress}%`,
              // react-native-web supports CSS background property
              // @ts-ignore
              background: 'linear-gradient(90deg, #FACF47, #3DAB9E)',
            },
          ]}
        />
      </View>

      <View style={styles.progressMeta}>
        <CCText fontSize={12} color="#888280">
          Question{' '}
          <CCText fontSize={12} fontWeight="bold" color="#262322">
            {currentIndex + 1}
          </CCText>{' '}
          of {questions.length}
        </CCText>
        <View style={[styles.tag, isMandatory ? styles.tagReq : styles.tagOpt]}>
          <CCText
            fontSize={10}
            fontWeight="bold"
            color={isMandatory ? '#806234' : '#888280'}
            style={styles.tagText}>
            {isMandatory ? 'REQUIRED' : 'OPTIONAL'}
          </CCText>
        </View>
      </View>

      {/* Question */}
      <View style={styles.questionSection}>
        {q.question_type !== 'consent' && (
          <CCText
            fontSize={19}
            fontWeight="semibold"
            color="#262322"
            style={styles.questionLabel}>
            {q.question_text}
          </CCText>
        )}

        {q.helper_text && (
          <CCText fontSize={12} color="#888280" style={styles.helperText}>
            {q.helper_text}
          </CCText>
        )}

        {/* Input */}
        <View style={styles.inputArea}>
          <QuestionInput question={q} value={val} onChange={setVal} />
        </View>
      </View>

      {/* Validation error */}
      {validationError && (
        <CCText fontSize={11} color="#CD2C39" style={{marginTop: 6}}>
          {validationError}
        </CCText>
      )}

      {/* Autofill */}
      {autofillValue && !answered && (
        <TouchableOpacity
          style={styles.autofillBtn}
          onPress={() => setVal(autofillValue)}>
          {/* <Octicons name="sparkle-fill" size={12} color="#806234" /> */}
          <Sparkle />
          <CCText fontSize={12} color="#4D3F37">
            Use from profile ·{' '}
            <CCText fontSize={12} fontWeight="semibold" color="#262322">
              {autofillValue}
            </CCText>
          </CCText>
        </TouchableOpacity>
      )}

      {/* Footer */}
      <View style={styles.footer}>
        <TouchableOpacity
          style={[styles.backBtn, backDisabled && styles.backBtnDisabled]}
          disabled={backDisabled}
          onPress={() => {
            if (currentIndex > 0) {
              setCurrentIndex(i => i - 1);
              setValidationError(null);
            } else if (onBack) {
              onBack();
            }
          }}>
          <Ionicons name="arrow-back" size={14} color="#4D3F37" />
          <CCText fontSize={13} fontWeight="semibold" color="#4D3F37">
            Back
          </CCText>
        </TouchableOpacity>

        {!isMandatory && !answered && !isLast && (
          <TouchableOpacity
            style={styles.skipBtn}
            onPress={() => setCurrentIndex(i => i + 1)}>
            <CCText
              fontSize={13}
              fontWeight="medium"
              color="#888280"
              style={styles.skipText}>
              Skip
            </CCText>
          </TouchableOpacity>
        )}

        <TouchableOpacity
          style={[styles.nextBtn, nextDisabled && styles.nextBtnDisabled]}
          disabled={nextDisabled}
          onPress={validateAndProceed}
          activeOpacity={0.8}>
          <CCText
            fontSize={14}
            fontWeight="bold"
            color={nextDisabled ? 'rgba(77,63,55,0.42)' : '#262322'}>
            {isLast ? 'Submit & Proceed' : 'Next'}
          </CCText>
          <Ionicons
            name="arrow-forward"
            size={16}
            color={nextDisabled ? 'rgba(77,63,55,0.42)' : '#262322'}
          />
        </TouchableOpacity>
      </View>
    </Box>
  );
}

const styles = StyleSheet.create({
  progressTrack: {
    marginTop: 18,
    height: 6,
    backgroundColor: 'rgba(77, 63, 55, 0.10)',
    borderRadius: 999,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 999,
  },
  progressMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 10,
  },
  tag: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
  },
  tagReq: {
    backgroundColor: '#FAF0D1',
  },
  tagOpt: {
    backgroundColor: '#F5EDD3',
  },
  tagText: {
    letterSpacing: 0.06 * 10, // 0.06em at fontSize 10
    textTransform: 'uppercase',
  },
  questionSection: {
    marginTop: 22,
  },
  questionLabel: {
    lineHeight: 24.7, // fontSize 19 * 1.3
  },
  helperText: {
    marginTop: 6,
    lineHeight: 18, // fontSize 12 * 1.5
  },
  inputArea: {
    marginTop: 16,
  },
  inputLg: {
    paddingVertical: 14,
    paddingHorizontal: 16,
    fontSize: 15,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: 'rgba(77, 63, 55, 0.18)',
  },
  textareaInput: {
    minHeight: 100,
  },
  selectWrap: {
    gap: 6,
  },
  choiceWrap: {
    gap: 6,
  },
  choiceCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 11,
    paddingHorizontal: 14,
    backgroundColor: '#FFFFFF',
    borderWidth: 1.5,
    borderColor: 'rgba(38, 35, 34, 0.10)',
    borderRadius: 12,
  },
  choiceCardSel: {
    borderColor: '#D1AB41',
    backgroundColor: '#FFFDF5',
    // @ts-ignore — react-native-web supports boxShadow
    boxShadow: '0 0 0 3px rgba(250, 207, 71, 0.14)',
  },
  radioOuter: {
    width: 18,
    height: 18,
    borderRadius: 999,
    borderWidth: 2,
    borderColor: 'rgba(77, 63, 55, 0.25)',
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioOuterSel: {
    borderColor: '#D1AB41',
  },
  radioDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    backgroundColor: '#D1AB41',
  },
  multiWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  multiChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 9,
    paddingRight: 14,
    paddingLeft: 11,
    backgroundColor: '#FFFFFF',
    borderWidth: 1.5,
    borderColor: 'rgba(38, 35, 34, 0.10)',
    borderRadius: 999,
  },
  multiChipSel: {
    borderColor: '#3DAB9E',
    backgroundColor: '#E4F6EB',
  },
  checkOuter: {
    width: 18,
    height: 18,
    borderRadius: 6,
    borderWidth: 1.5,
    borderColor: 'rgba(77, 63, 55, 0.30)',
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkOuterSel: {
    backgroundColor: '#3DAB9E',
    borderColor: '#3DAB9E',
  },
  consentRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
    backgroundColor: '#FAF6EB',
    borderWidth: 1.5,
    borderColor: 'rgba(77, 63, 55, 0.16)',
    borderRadius: 12,
  },
  consentRowSel: {
    backgroundColor: '#E4F6EB',
    borderColor: '#3DAB9E',
  },
  consentText: {
    flex: 1,
    lineHeight: 19.5, // fontSize 13 * 1.5
  },
  autofillBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-start',
    marginTop: 10,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#FAF0D1',
    borderWidth: 1,
    borderColor: 'rgba(209, 171, 65, 0.6)',
    borderStyle: 'dashed',
    borderRadius: 10,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: 22,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(38, 35, 34, 0.10)',
  },
  backBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'transparent',
    paddingVertical: 8,
  },
  backBtnDisabled: {
    opacity: 0.3,
  },
  skipBtn: {
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  skipText: {
    textDecorationLine: 'underline',
    // @ts-ignore — react-native-web supports textUnderlineOffset
    textUnderlineOffset: 3,
  },
  nextBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginLeft: 'auto',
    backgroundColor: '#FACF47',
    borderRadius: 32,
    paddingVertical: 13,
    paddingHorizontal: 20,
    // @ts-ignore — react-native-web supports boxShadow
    boxShadow: '0 4px 8px rgba(209, 171, 65, 0.35)',
  },
  nextBtnDisabled: {
    backgroundColor: '#FEF2B5',
    // @ts-ignore
    boxShadow: 'none',
  },
});
